import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /**
   * Bound once so `removeEventListener` can actually remove it.
   * BNQ-WEB-0408 (Sprint 3.4.13 EXT): `disconnectedCallback` used to pass
   * `this.#updateQuickAddButtonState.bind(this)`, and `.bind()` returns a NEW function every call —
   * so the handler added in `connectedCallback` was never removed and one `variantSelected`
   * listener leaked per teardown (paginated grids, section morphs, Theme Editor reloads).
   * @type {(event: Event) => void}
   */
  #onVariantSelected = (event) => this.#updateQuickAddButtonState(/** @type {any} */ (event));
  /** Guards against a second fetch while one is already in flight. @type {boolean} */
  #loading = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    // BNQ-WEB-0408: null-guarded. A product rendered without a variant picker (single-variant, or a
    // template that omits the block) would otherwise throw here the moment the fetch starts
    // succeeding — a crash that was invisible for as long as `productGrid` was always null.
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click.
   *
   * BNQ-WEB-0407 (Sprint 3.4.13 EXT) — THE DIALOG IS NOW OPENED ONLY WITH CONTENT IN HAND.
   *
   * Previously `#openQuickAddModal()` was called unconditionally at the end of this method, outside
   * the `if (productGrid)` branch. Because `[data-product-grid-content]` did not exist in this
   * theme's product template, `productGrid` was ALWAYS undefined — so every "Choose" click skipped
   * the render and opened an empty dialog against `min-height: 500px`: the blank white box.
   *
   * Restoring the attribute fixes the data path, but that alone would leave the same failure mode
   * available whenever a fetch genuinely fails (offline, 404 on an unpublished product, aborted
   * request). The open is therefore GATED on resolved content, and the failure path falls back to
   * the product page — which is where the visitor was trying to get to anyway. The empty dialog is
   * now unreachable by construction rather than by luck.
   *
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#loading) return;

    // `event.target` rather than `currentTarget`: this handler is async and `currentTarget` is only
    // meaningful during dispatch, so it can be null by the time the fetch resolves. Falls back to
    // the component's own Choose button when the click originated on a nested span.
    const trigger = /** @type {HTMLButtonElement | null} */ (
      (event.target instanceof HTMLElement ? event.target.closest('button') : null) ??
        this.querySelector('.quick-add__button--choose')
    );
    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      this.#setLoading(true, trigger);

      try {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      } catch (error) {
        console.error('[quick-add] Failed to load product content', error);
      } finally {
        this.#setLoading(false, trigger);
      }
    }

    if (!productGrid) {
      // No content — never open an empty dialog. Announce, then hand the visitor to the product
      // page, which is the destination the "Choose" affordance stands in for.
      this.#announce(this.dataset.errorText || '');
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    this.#openQuickAddModal(trigger);
  };

  /**
   * Reflects the in-flight fetch on the trigger and announces it.
   * @param {boolean} loading
   * @param {HTMLButtonElement | null} trigger
   */
  #setLoading(loading, trigger) {
    this.#loading = loading;
    this.toggleAttribute('loading', loading);
    // `aria-busy` is a string-valued ARIA attribute, so it is set rather than toggled; and the
    // button is NOT `disabled` — disabling the element that currently holds focus drops focus to
    // <body>, which would break the focus-return contract the moment the dialog opens. Re-entry is
    // already prevented by the `#loading` guard at the top of handleClick.
    if (trigger) trigger.setAttribute('aria-busy', String(loading));
    if (loading) this.#announce(this.dataset.loadingText || '');
  }

  /**
   * Writes to the live region already present in the quick-add form, so loading and error states are
   * announced rather than only drawn.
   * @param {string} message
   */
  #announce(message) {
    if (!message) return;
    const liveRegion = this.querySelector('[data-quick-add-status]');
    if (liveRegion) liveRegion.textContent = message;
  }

  /**
   * Keeps the card's button visible while its dialog is open, and RETURNS FOCUS TO THAT BUTTON when
   * the dialog closes.
   *
   * BNQ-WEB-0409 (Sprint 3.4.13 EXT) — the focus return is explicit because nothing else provides
   * it. `DialogComponent` (assets/dialog.js) has no focus-restoration logic of any kind, and the
   * browser's own `showModal()` restoration did not fire here: measured after Escape, focus landed
   * on <body>, so a keyboard visitor was returned to the top of the document and had to tab back
   * through the entire grid to reach the card they were on.
   *
   * `preventScroll` matters — `closeDialog()` restores the page's scroll position itself, and a
   * focus-driven scroll racing that restoration is what makes a grid jump on close.
   *
   * @param {QuickAddDialog} dialogComponent
   * @param {HTMLElement | null} trigger - The control that opened the dialog.
   */
  #stayVisibleUntilDialogCloses(dialogComponent, trigger) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        // Focus first, while the button is still forced visible by `stay-visible`; a control that
        // is display:none at the moment of focus() silently keeps focus on <body>.
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
        this.toggleAttribute('stay-visible', false);
      },
      { once: true }
    );
  }

  /** @param {HTMLElement | null} [trigger] - The control that opened the dialog, for focus return. */
  #openQuickAddModal = (trigger = null) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent, trigger);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    /**
     * BNQ-WEB-0410 (Sprint 3.4.13 EXT) — restore the buy-box styling scope on mobile.
     *
     * The mobile branch above deliberately lifts the variant picker and the product form OUT of
     * `.product-details` and then removes that wrapper, to rebuild the panel as a bottom sheet.
     * `.product-details` on this theme also carries `bnq-product-details`, which is the scope EVERY
     * BONQUERÉ buy-box rule is written against (`snippets/product-information-content.liquid`).
     * Removing the wrapper therefore silently dropped all of them, and the mobile dialog fell back
     * to Horizon defaults: Add to cart rendered in the theme's imperial blue (measured
     * rgb(31,42,106)) instead of charcoal, with a rgb(47,47,49) label on top of it — 1.4:1, an
     * unreadable primary action — and the selected size chip lost its white-on-charcoal text.
     *
     * Re-applying the class to the container restores the descendant scope without duplicating a
     * single declaration, so the mobile sheet and the PDP cannot drift apart. Desktop is untouched:
     * there the real `.product-details.bnq-product-details` wrapper is still present in the morphed
     * content, so this branch never runs.
     */
    if (isMobileBreakpoint()) modalContent.classList.add('bnq-product-details');
    else modalContent.classList.remove('bnq-product-details');

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
